/**
 * useTracking — Lightweight generic event tracking hook.
 *
 * Generates a persistent session_id (survives page reloads, resets on tab close).
 * Provides a single `track(event, source, meta?)` function.
 * City is auto-pulled from metroStore.
 */
import { useCallback, useMemo } from 'react';
import { useMetroStore } from '../stores/metroStore';
import { useAuthStore } from '../stores/authStore';
import { ensureAnonymousSessionId } from '@/utils/anonymousSessionId';
import { fetchWithTimeout } from '@/utils/fetchWithTimeout';

const API = window.location.origin;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA4_EVENT_FIELDS: Record<string, readonly string[]> = {
  comparison_opened: ['building_count', 'decision_path', 'question_origin'],
  comparison_building_added: ['building_slug', 'building_count'],
  comparison_building_removed: ['building_slug', 'building_count', 'decision_path', 'question_origin'],
  comparison_ai_explained: ['building_count', 'decision_path', 'question_origin'],
  comparison_ai_unavailable: ['building_count', 'decision_path', 'question_origin'],
  comparison_shared: ['decision_path', 'question_origin'],
  comparison_handoff_copied: ['decision_path', 'question_origin'],
  comparison_path_selected: ['decision_path', 'building_count'],
  question_opened: ['question_slug', 'question_cluster'],
  question_answer_viewed: ['question_slug', 'question_cluster'],
  question_action_clicked: ['question_slug', 'question_cluster', 'action_kind', 'action_label'],
  question_cluster_filtered: ['question_cluster'],
  question_search_started: ['question_slug', 'question_cluster'],
  question_search_submitted: ['question_slug', 'question_cluster'],
  question_handoff_started: ['question_slug', 'question_cluster'],
  question_handoff_submitted: ['question_slug', 'question_cluster', 'tour_id'],
  neighborhood_match_started: ['match_id', 'answered_count', 'vibe_count'],
  neighborhood_match_completed: ['match_id', 'result_count', 'top_neighborhood'],
  neighborhood_match_failed: ['match_id'],
  neighborhood_match_result_selected: ['match_id', 'neighborhood', 'result_rank'],
  neighborhood_match_handoff_clicked: ['match_id'],
  neighborhood_match_search_started: ['match_id', 'neighborhood'],
  neighborhood_match_search_submitted: ['match_id', 'neighborhood'],
  neighborhood_match_handoff_started: ['match_id'],
  neighborhood_match_handoff_submitted: ['match_id', 'tour_id'],
  ask_taco_question_submitted: ['question_family', 'has_memory'],
  ask_taco_answer_viewed: ['question_family', 'freshness_status', 'citation_count', 'fair_housing_status', 'explanation_mode'],
  ask_taco_answer_failed: [],
  ask_taco_action_clicked: ['action_kind', 'question_family'],
  ask_taco_handoff_started: ['question_family'],
  ask_taco_handoff_submitted: ['tour_id'],
  ask_taco_memory_cleared: [],
  vibe_capture_viewed: ['reason'],
  vibe_capture_focused: ['reason'],
  vibe_capture_submit_attempted: ['reason'],
  vibe_capture_validation_failed: ['reason'],
  vibe_capture_dismissed: ['reason'],
  lead_captured: ['reason', 'has_phone', 'has_email'],
};

/** Send the same event to the consent-aware Google tag. The GTM bootstrap
 * queues calls made before its container is ready, while Consent Mode decides
 * whether Analytics may use storage. Internal /api/track remains independent. */
export function forwardToGoogleAnalytics(
  event: string,
  source: string,
  city: string | null,
  meta?: Record<string, unknown>,
) {
  if (typeof window.gtag !== 'function') return;
  const allowedFields = GA4_EVENT_FIELDS[event];
  if (!allowedFields) return;
  const approvedMeta = Object.fromEntries(
    allowedFields
      .filter((field) => meta?.[field] !== undefined)
      .map((field) => [field, meta?.[field]]),
  );
  window.gtag('event', event, {
    event_source: source,
    city: city || undefined,
    ...approvedMeta,
  });
}

function getSessionId(): string {
  const sid = ensureAnonymousSessionId();
  // Expose on window for non-hook callers (authStore, signup attribution helper).
  if (typeof window !== 'undefined') {
    (window as { __ts_session_id?: string }).__ts_session_id = sid;
  }
  return sid;
}

/** Fire-and-forget POST to /api/track. Never throws. */
function fireEvent(
  event: string,
  source: string,
  city: string | null,
  sessionId: string,
  userId: string | null,
  meta?: Record<string, unknown>,
) {
  forwardToGoogleAnalytics(event, source, city, meta);
  const token = useAuthStore.getState().token;
  fetchWithTimeout(`${API}/api/track`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      event,
      source,
      city,
      session_id: sessionId,
      user_id: userId,
      meta: meta || {},
    }),
  }).catch(() => {});
}

/** Non-hook tracking entry for store/util callers (e.g. the canonical
 *  save toggle in savedListUtils). Same wire event as useTracking().track;
 *  metro + user pulled from Zustand outside React. Never throws. */
export function trackEvent(event: string, source: string, meta?: Record<string, unknown>) {
  try {
    const city = useMetroStore.getState().selectedMetro;
    const userId = useAuthStore.getState().user?.user_id || null;
    fireEvent(event, source, city, getSessionId(), userId, meta);
  } catch {
    // tracking must never break the action it decorates
  }
}


export function useTracking() {
  const selectedMetro = useMetroStore((s) => s.selectedMetro);
  const user = useAuthStore((s) => s.user);

  const sessionId = useMemo(() => getSessionId(), []);
  const userId = user?.user_id || null;

  const track = useCallback(
    (event: string, source: string, meta?: Record<string, unknown>) => {
      fireEvent(event, source, selectedMetro, sessionId, userId, meta);
    },
    [selectedMetro, sessionId, userId],
  );

  return { track, sessionId };
}
